import React, { type FC, useMemo } from 'react';
import { Mails, Inbox } from 'lucide-react';
import List from '@mui/material/List';

import NavigationLink from 'src/components/layout/landlord/NavigationLink';
import { useInboxes } from 'src/services/api/inboxes/use-inboxes';
import { useInboxFavorites } from 'src/services/api/inboxes/use-inbox-favorites';
import { usePrimaryInbox } from 'src/services/api/inboxes/use-primary-inbox';

import styles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';

type Props = {
    onNavigationChange: () => void;
    isSidebarCollapsed: boolean;
}

export const DesktopNavigationInboxesLink: FC<Props> = ({ onNavigationChange, isSidebarCollapsed }) => {
    const { data: inboxes } = useInboxes();
    const { data: viewFavorites } = useInboxFavorites();
    const { primaryInboxId } = usePrimaryInbox();

    const childrenViews = useMemo(() => {
        if (!inboxes || !viewFavorites) {
            return [];
        }
        const favoriteViewIDs = new Set(viewFavorites.map(fav => fav.inbox_id));

        const teamInbox =
            <NavigationLink
                text={isSidebarCollapsed ? '' : 'Team Inbox'}
                icon={<Mails />}
                link={'/landlord/inner/inboxes/default'}
                onClick={onNavigationChange}
            />;

        if (primaryInboxId === 'default') {
            const favorites = inboxes.filter(inbox => favoriteViewIDs.has(inbox.id)).map(view => {
                return (
                    <NavigationLink
                        key={view.id}
                        text={view.name}
                        icon={<Mails />}
                        link={`/landlord/inner/inboxes/${view.id}`}
                        onClick={onNavigationChange}
                    />
                );
            });
            return [teamInbox, ...favorites];
        }

        const primaryInbox = inboxes.find(inbox => inbox.id === primaryInboxId);

        if (!primaryInbox) {
            const favorites = inboxes.filter(inbox => favoriteViewIDs.has(inbox.id)).map(view => {
                return (
                    <NavigationLink
                        key={view.id}
                        text={view.name}
                        icon={<Mails />}
                        link={`/landlord/inner/inboxes/${view.id}`}
                        onClick={onNavigationChange}
                    />
                );
            });
            return [teamInbox, ...favorites];
        }

        const primaryInboxLink = (
            <NavigationLink
                key={primaryInbox.id}
                text={primaryInbox.name}
                icon={<Mails />}
                link={`/landlord/inner/inboxes/${primaryInbox.id}`}
                onClick={onNavigationChange}
            />
        );

        const favorites = inboxes
            .filter(inbox => inbox.id !== primaryInbox.id && favoriteViewIDs.has(inbox.id))
            .map(view => {
                return (
                    <NavigationLink
                        key={view.id}
                        text={view.name}
                        icon={<Mails />}
                        link={`/landlord/inner/inboxes/${view.id}`}
                        onClick={onNavigationChange}
                    />
                );
            });

        return [primaryInboxLink, teamInbox, ...favorites];
    }, [inboxes, viewFavorites, primaryInboxId, isSidebarCollapsed]);

    return (
        <>
            <NavigationLink
                text={isSidebarCollapsed ? '' : 'Inboxes'}
                icon={<Inbox />}
                link="/landlord/inner/inboxes"
                onClick={onNavigationChange}
            />
            {!isSidebarCollapsed && (<List className={styles.inboxLinks}>
                {childrenViews}
            </List>)}
        </>);
};
